import { AlertTriangle, RefreshCcw, XCircle } from 'lucide-react';
import SummaryCard from './SummaryCard';

const DryRunStep = ({ dryRunData, summaryCards, onReset, onContinue }) => {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {summaryCards.map((card) => (
          <SummaryCard key={card.label} {...card} />
        ))}
      </div>

      {dryRunData.warnings?.length > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="mb-2 flex items-center gap-2 font-medium text-yellow-800">
            <AlertTriangle className="h-4 w-4" />
            Warnings
          </div>
          <div className="space-y-1 text-sm text-yellow-800">
            {dryRunData.warnings.map((warning) => (
              <div key={warning}>{warning}</div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-bg-surface p-6">
        <h2 className="text-lg font-semibold text-text-primary">Review dry-run output</h2>
        <p className="mt-1 text-sm text-text-secondary">
          Check invalid rows and duplicates before moving to the final import summary.
        </p>

        {dryRunData.summary.invalidCount > 0 && (
          <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
            Invalid rows can still be part of the import job, but they are likely to fail during processing. Fix the spreadsheet first if you want a cleaner result set.
          </div>
        )}

        {dryRunData.summary.duplicateCount > 0 && (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
            Duplicate matches were found in the database. Bukito will update those existing books during the import flow.
          </div>
        )}

        <div className="mt-6 grid gap-6 xl:grid-cols-2">
          <div>
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-text-primary">
              <XCircle className="h-4 w-4 text-red-500" />
              Invalid Rows ({dryRunData.invalidRows.length})
            </div>
            <div className="space-y-3">
              {dryRunData.invalidRows.length === 0 ? (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                  No invalid rows found.
                </div>
              ) : (
                dryRunData.invalidRows.map((row) => (
                  <div key={row.rowNumber} className="rounded-lg border border-gray-200 p-4">
                    <div className="text-sm font-medium text-text-primary">Row {row.rowNumber}</div>
                    <div className="mt-2 text-sm text-red-600">{row.errors.join(', ')}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-text-primary">
              <RefreshCcw className="h-4 w-4 text-yellow-600" />
              Duplicates ({dryRunData.duplicates.length})
            </div>
            <div className="space-y-3">
              {dryRunData.duplicates.length === 0 ? (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                  No duplicates detected.
                </div>
              ) : (
                dryRunData.duplicates.map((row) => (
                  <div key={`${row.rowIndex}-${row.existingBookId}`} className="rounded-lg border border-gray-200 p-4">
                    <div className="text-sm font-medium text-text-primary">Row {row.rowIndex}</div>
                    <div className="mt-1 text-sm text-text-secondary">{row.title}</div>
                    <div className="mt-1 text-xs text-text-secondary">Existing Book ID: {row.existingBookId}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={onReset}
            className="rounded-lg border border-gray-200 px-4 py-2 text-text-primary hover:bg-gray-50"
          >
            Start Over
          </button>
          <button
            onClick={onContinue}
            className="rounded-lg bg-primary-500 px-4 py-2 text-white hover:bg-primary-600"
          >
            Continue to Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default DryRunStep;
